// src/Dashboard/AdminDashboard/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaChartLine,
  FaChartPie,
  FaClipboardList,
  FaFilter,
  FaGasPump,
  FaOilCan,
  FaSearch,
  FaTachometerAlt,
  FaTruck,
} from "react-icons/fa";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./AdminDashboard.css";
import Sidebar from "../../Component/Sidebar/Sidebar";

const parseOperationDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateKey = (date) => {
  if (!date) return "";
  return date.toISOString().split("T")[0];
};

const formatShortDate = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getStatus = (item) => String(item?.status || "Unknown").trim() || "Unknown";

const maxValue = (items) =>
  Math.max(...items.map((item) => Number(item.value || 0)), 1);

const PieChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (!total) {
    return <div className="chart-empty">No data available</div>;
  }

  let current = 0;
  const gradient = data
    .map((item) => {
      const start = current;
      current += (item.value / total) * 100;
      return `${item.color} ${start}% ${current}%`;
    })
    .join(", ");

  return (
    <div className="pie-chart-wrap">
      <div
        className="pie-chart"
        style={{ background: `conic-gradient(${gradient})` }}
      >
        <span>{total}</span>
      </div>
      <div className="chart-legend">
        {data.map((item) => (
          <span key={item.label}>
            <i style={{ background: item.color }} />
            {item.label}: {item.value}
          </span>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({ data, unit = "L" }) => {
  const max = maxValue(data);

  if (!data.length) {
    return <div className="chart-empty">No data available</div>;
  }

  return (
    <div className="bar-chart">
      {data.map((item, index) => (
        <div className="bar-row" key={item.label}>
          <span className="bar-label">{item.label}</span>
          <div className="bar-track">
            <span
              className={`bar-fill color-${index % 5}`}
              style={{ width: `${Math.max((item.value / max) * 100, 6)}%` }}
            />
          </div>
          <strong>
            {item.value}
            {unit ? ` ${unit}` : ""}
          </strong>
        </div>
      ))}
    </div>
  );
};

const TrendChart = ({ data }) => {
  const max = maxValue(data);
  const points = data.map((item, index) => {
    const x = data.length === 1 ? 50 : (index / (data.length - 1)) * 100;
    const y = 100 - (Number(item.value || 0) / max) * 82 - 8;
    return `${x},${y}`;
  });

  return (
    <div className="trend-chart">
      <svg viewBox="0 0 100 108" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trendGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" />
            <stop offset="52%" stopColor="#14b8a6" />
            <stop offset="100%" stopColor="#f97316" />
          </linearGradient>
        </defs>
        <polyline points={points.join(" ")} />
        {points.map((point, index) => {
          const [cx, cy] = point.split(",");
          return <circle key={data[index].key} cx={cx} cy={cy} r="1.8" />;
        })}
      </svg>
      <div className="trend-labels">
        {data.map((item) => (
          <span key={item.key}>
            <b>{item.value}L</b>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [totalTrucks, setTotalTrucks] = useState(0);
  const [totalTankers, setTotalTankers] = useState(0);
  const [activeTrucks, setActiveTrucks] = useState(0);
  const [oilFilledToday, setOilFilledToday] = useState(0);
  const [maintenanceVehicles, setMaintenanceVehicles] = useState(0);
  const [trucks, setTrucks] = useState([]);
  const [tankers, setTankers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterField, setFilterField] = useState("vehicleId");
  const [operations, setOperations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const todayISO = useMemo(() => new Date().toISOString().split("T")[0], []);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);

        const trucksRef = collection(db, "trucks");
        const tankersRef = collection(db, "tankers");
        const [allSnap, allTankersSnap] = await Promise.all([
          getDocs(trucksRef),
          getDocs(tankersRef),
        ]);

        const truckRows = allSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const tankerRows = allTankersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setTrucks(truckRows);
        setTankers(tankerRows);
        setTotalTrucks(allSnap.size);
        setTotalTankers(allTankersSnap.size);

        const activeSnap = await getDocs(
          query(trucksRef, where("status", "==", "Active"))
        );
        setActiveTrucks(activeSnap.size);

        const maintenanceSnap = await getDocs(
          query(trucksRef, where("status", "==", "Maintenance"))
        );
        setMaintenanceVehicles(maintenanceSnap.size);

        const tankerRef = collection(db, "tankerFillOperations");
        const tankerSnap = await getDocs(tankerRef);

        let todayOil = 0;
        tankerSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const dateKey = formatDateKey(parseOperationDate(data.dateTime));
          if (dateKey === todayISO) {
            todayOil += Number(data.filledOil || 0);
          }
        });
        setOilFilledToday(todayOil);

        const pumpRef = collection(db, "tankerFillOperationsPump");
        const truckFillRef = collection(db, "tankerFillOperations");
        const [pumpSnap, truckFillSnap] = await Promise.all([
          getDocs(query(pumpRef, orderBy("createdAt", "desc"), limit(80))),
          getDocs(query(truckFillRef, orderBy("createdAt", "desc"), limit(80))),
        ]);

        const pumpOps = pumpSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const date = parseOperationDate(data.dateReceived || data.createdAt);

          return {
            id: docSnap.id,
            type: "Tanker Fill",
            tankerId: data.tankerId || "",
            vehicleId: "-",
            driverName: data.driverName || "",
            city: data.pumpName || "",
            dateTime: data.dateReceived || "",
            quantity: Number(data.filledOil || 0),
            date,
          };
        });

        const truckOps = truckFillSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const date = parseOperationDate(data.dateTime || data.createdAt);

          return {
            id: docSnap.id,
            type: "Vehicle Fill",
            tankerId: data.tankerId || "",
            vehicleId: data.vehicleId || data.truckId || "",
            driverName: data.driverName || "",
            city: data.source || "",
            dateTime: data.dateTime || "",
            quantity: Number(data.filledOil || 0),
            date,
          };
        });

        const merged = [...pumpOps, ...truckOps]
          .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
          .slice(0, 80);

        setOperations(merged);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [todayISO]);

  const filteredOperations = useMemo(() => {
    if (!searchText.trim()) return operations.slice(0, 20);

    const term = searchText.toLowerCase();
    return operations
      .filter((op) => {
        if (filterField === "vehicleId") {
          return String(op.vehicleId).toLowerCase().includes(term);
        }
        if (filterField === "tankerId") {
          return String(op.tankerId).toLowerCase().includes(term);
        }
        if (filterField === "driverName") {
          return String(op.driverName).toLowerCase().includes(term);
        }
        if (filterField === "city") {
          return String(op.city).toLowerCase().includes(term);
        }
        return false;
      })
      .slice(0, 20);
  }, [operations, filterField, searchText]);

  const chartData = useMemo(() => {
    const sumBy = (items, keyGetter, valueGetter) => {
      const map = new Map();
      items.forEach((item) => {
        const key = keyGetter(item) || "Unknown";
        map.set(key, (map.get(key) || 0) + Number(valueGetter(item) || 0));
      });
      return Array.from(map, ([label, value]) => ({ label, value })).sort(
        (a, b) => b.value - a.value
      );
    };

    const fleetMix = [
      { label: "Vehicles", value: totalTrucks, color: "#2563eb" },
      { label: "Tankers", value: totalTankers, color: "#14b8a6" },
      { label: "Maintenance", value: maintenanceVehicles, color: "#f97316" },
    ].filter((item) => item.value > 0);

    const operationTypes = sumBy(
      operations,
      (op) => op.type,
      (op) => op.quantity
    ).map((item, index) => ({
      ...item,
      color: ["#2563eb", "#14b8a6", "#f97316"][index % 3],
    }));

    const statusSplit = sumBy(
      [...trucks, ...tankers],
      (item) => getStatus(item),
      () => 1
    ).map((item, index) => ({
      ...item,
      color: ["#22c55e", "#f97316", "#64748b", "#ef4444"][index % 4],
    }));

    const topTankers = sumBy(
      operations.filter((op) => op.tankerId),
      (op) => op.tankerId,
      (op) => op.quantity
    ).slice(0, 5);

    const topVehicles = sumBy(
      operations.filter((op) => op.vehicleId && op.vehicleId !== "-"),
      (op) => op.vehicleId,
      (op) => op.quantity
    ).slice(0, 5);

    const oilTrend = Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - index));
      const key = formatDateKey(date);
      return {
        label: formatShortDate(key),
        key,
        value: 0,
      };
    });

    operations.forEach((op) => {
      const key = formatDateKey(op.date || parseOperationDate(op.dateTime));
      const match = oilTrend.find((day) => day.key === key);
      if (match) match.value += Number(op.quantity || 0);
    });

    return {
      fleetMix,
      operationTypes,
      oilTrend,
      statusSplit,
      topTankers,
      topVehicles,
    };
  }, [maintenanceVehicles, operations, tankers, totalTankers, totalTrucks, trucks]);

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="main-content">
        <h1>Dashboard Overview</h1>

        <div className="cards">
          <div className="card trucks">
            <div className="card-icon">
              <FaTruck />
            </div>
            <h3>Total Vehicle</h3>
            <p>{totalTrucks}</p>
          </div>

          <div className="card tankers">
            <div className="card-icon">
              <FaOilCan />
            </div>
            <h3>Total Tanker</h3>
            <p>{totalTankers}</p>
          </div>

          <div className="card active">
            <div className="card-icon">
              <FaTachometerAlt />
            </div>
            <h3>Active Vehicle</h3>
            <p>{activeTrucks}</p>
          </div>

          <div className="card oil">
            <div className="card-icon">
              <FaGasPump />
            </div>
            <h3>Oil Filled Today</h3>
            <p>{oilFilledToday} L</p>
          </div>

          <div className="card pending">
            <div className="card-icon">
              <FaClipboardList />
            </div>
            <h3>Maintenance Vehicle</h3>
            <p>{maintenanceVehicles}</p>
          </div>
        </div>

        <section className="analytics-section">
          <div className="section-title-row">
            <div>
              <h2>Analytics Dashboard In Graph</h2>
              <p>Fleet, tanker and vehicle oil movement visual summary</p>
            </div>
            {isLoading && <span className="small-badge">Updating graphs...</span>}
          </div>

          <div className="chart-grid">
            <article className="chart-card chart-wide">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaChartLine />
                </span>
                <div>
                  <h3>7 Day Oil Filling Trend</h3>
                  <p>Daily litres from tanker and vehicle operations</p>
                </div>
              </div>
              <TrendChart data={chartData.oilTrend} />
            </article>

            <article className="chart-card">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaChartPie />
                </span>
                <div>
                  <h3>Fleet Distribution</h3>
                  <p>Vehicle, tanker and maintenance count</p>
                </div>
              </div>
              <PieChart data={chartData.fleetMix} />
            </article>

            <article className="chart-card">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaGasPump />
                </span>
                <div>
                  <h3>Oil Flow By Operation</h3>
                  <p>Tanker fill vs vehicle fill volume</p>
                </div>
              </div>
              <PieChart data={chartData.operationTypes} />
            </article>

            <article className="chart-card">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaOilCan />
                </span>
                <div>
                  <h3>Top Tankers</h3>
                  <p>Most oil handled by tanker</p>
                </div>
              </div>
              <BarChart data={chartData.topTankers} />
            </article>

            <article className="chart-card">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaTruck />
                </span>
                <div>
                  <h3>Top Vehicles</h3>
                  <p>Vehicles receiving highest oil</p>
                </div>
              </div>
              <BarChart data={chartData.topVehicles} />
            </article>

            <article className="chart-card">
              <div className="chart-card-header">
                <span className="chart-icon">
                  <FaClipboardList />
                </span>
                <div>
                  <h3>Fleet Status</h3>
                  <p>Active, maintenance and unknown status</p>
                </div>
              </div>
              <BarChart data={chartData.statusSplit} unit="" />
            </article>
          </div>
        </section>

        <div className="filter-search-row">
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select
              value={filterField}
              onChange={(e) => setFilterField(e.target.value)}
            >
              <option value="vehicleId">Vehicle ID</option>
              <option value="tankerId">Tanker ID</option>
              <option value="driverName">Driver Name</option>
              <option value="city">City</option>
            </select>
          </div>

          <div className="search-group">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder={`Search by ${filterField === "vehicleId"
                ? "Vehicle ID"
                : filterField === "tankerId"
                  ? "Tanker ID"
                  : filterField === "driverName"
                    ? "Driver Name"
                    : "City"
                }`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="operations-table">
          <div className="table-header-row">
            <h3>Recent Operations</h3>
            {isLoading && <span className="small-badge">Loading...</span>}
          </div>

          {filteredOperations.length === 0 && !isLoading ? (
            <p className="empty-text">No operations found for selected filter.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Vehicle ID</th>
                  <th>Tanker ID</th>
                  <th>Driver</th>
                  <th>City / Location</th>
                  <th>Filled (L)</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredOperations.map((op) => (
                  <tr key={op.id}>
                    <td>{op.type}</td>
                    <td>{op.vehicleId || "-"}</td>
                    <td>{op.tankerId || "-"}</td>
                    <td>{op.driverName || "-"}</td>
                    <td>{op.city || "-"}</td>
                    <td>{op.quantity || 0}</td>
                    <td>{op.dateTime || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
